/**
 * StopListItem - A single draggable stop row in the route builder stop list.
 *
 * Props:
 *   stop        {object}   - stop data (name, address, estimatedArrival, serviceMinutes, type, order)
 *   index       {number}   - position in the list (used by Draggable)
 *   isSelected  {bool}     - whether this stop is currently selected
 *   onSelect    {function} - called with stop when clicked
 */

import React from 'react'
import { Draggable } from 'react-beautiful-dnd'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Chip from '@material-ui/core/Chip'
import IconButton from '@material-ui/core/IconButton'
import DragHandleIcon from '@material-ui/icons/DragHandle'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import EditLocationIcon from '@material-ui/icons/EditLocation'
import AccessTimeIcon from '@material-ui/icons/AccessTime'

const { formatTime } = require('./routeHelpers')

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 1, 1, 0.5),
    marginBottom: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    border: '2px solid transparent',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    '&:hover': {
      boxShadow: theme.shadows[2],
    },
  },
  rootSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: theme.shadows[2],
  },
  rootDragging: {
    boxShadow: theme.shadows[6],
    backgroundColor: theme.palette.grey[50],
  },
  dragHandle: {
    padding: theme.spacing(0.5),
    color: theme.palette.text.disabled,
    cursor: 'grab',
    flexShrink: 0,
    '&:active': {
      cursor: 'grabbing',
    },
  },
  locationIcon: {
    flexShrink: 0,
    marginRight: theme.spacing(1),
  },
  locationIconJobsite: {
    color: theme.palette.primary.main,
  },
  locationIconManual: {
    color: theme.palette.secondary.main,
  },
  stopNumber: {
    flexShrink: 0,
    width: 20,
    textAlign: 'right',
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: '0.75rem',
  },
  stopInfo: {
    flex: 1,
    minWidth: 0,
  },
  stopName: {
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.3,
  },
  stopAddress: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    lineHeight: 1.3,
  },
  metaBlock: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    marginLeft: theme.spacing(1),
    gap: theme.spacing(0.5),
  },
  arrivalTime: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
  },
  durationChip: {
    height: 18,
    fontSize: '0.68rem',
    backgroundColor: theme.palette.grey[200],
  },
  actionChip: {
    height: 18,
    fontSize: '0.62rem',
    fontWeight: 600,
    marginTop: 2,
  },
}))

const ACTION_TYPE_CONFIG = {
  work_ticket_visit: { label: 'Work Order', color: '#1565C0', bg: '#E3F2FD' },
  maintenance_visit: { label: 'Maintenance', color: '#2E7D32', bg: '#E8F5E9' },
  irrigation_inspection: { label: 'Irrigation', color: '#0277BD', bg: '#E1F5FE' },
  herbicide_application: { label: 'Herbicide', color: '#F57F17', bg: '#FFF8E1' },
  site_audit: { label: 'Site Audit', color: '#6A1B9A', bg: '#F3E5F5' },
  site_walk: { label: 'Site Walk', color: '#4527A0', bg: '#EDE7F6' },
  lead_capture: { label: 'New Lead', color: '#00838F', bg: '#E0F7FA' },
  customer_meeting: { label: 'Meeting', color: '#4E342E', bg: '#EFEBE9' },
  issue_resolution: { label: 'Issue', color: '#C62828', bg: '#FFEBEE' },
  assigned_visit: { label: 'Assigned', color: '#AD1457', bg: '#FCE4EC' },
  generic: { label: 'Stop', color: '#546E7A', bg: '#ECEFF1' },
}

function StopListItem({ stop, index, isSelected, onSelect }) {
  const classes = useStyles()

  const isManual = stop.type === 'manual'
  const locationIconClass = isManual
    ? classes.locationIconManual
    : classes.locationIconJobsite
  const LocationIcon = isManual ? EditLocationIcon : LocationOnIcon

  const handleClick = () => {
    if (onSelect) onSelect(stop)
  }

  return (
    <Draggable draggableId={stop._id ? String(stop._id) : `stop-${index}`} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          onClick={handleClick}
          className={[
            classes.root,
            isSelected ? classes.rootSelected : '',
            snapshot.isDragging ? classes.rootDragging : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {/* Drag handle — only the icon handle triggers drag, not the whole row */}
          <span {...provided.dragHandleProps} className={classes.dragHandle}>
            <DragHandleIcon fontSize="small" />
          </span>

          {/* Stop number */}
          <Typography variant="caption" className={classes.stopNumber}>
            {stop.order || index + 1}
          </Typography>

          {/* Location type icon */}
          <LocationIcon
            fontSize="small"
            className={`${classes.locationIcon} ${locationIconClass}`}
          />

          {/* Name + address + action type */}
          <Box className={classes.stopInfo}>
            <Typography variant="body2" className={classes.stopName}>
              {stop.name || 'Unnamed Stop'}
            </Typography>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {stop.address ? (
                <Typography variant="caption" className={classes.stopAddress} style={{ flex: 1 }}>
                  {stop.address}
                </Typography>
              ) : null}
              {stop.actionType && stop.actionType !== 'generic' && (() => {
                const cfg = ACTION_TYPE_CONFIG[stop.actionType] || ACTION_TYPE_CONFIG.generic
                return (
                  <Chip
                    label={cfg.label}
                    size="small"
                    className={classes.actionChip}
                    style={{ color: cfg.color, backgroundColor: cfg.bg, borderColor: cfg.color, border: '1px solid' }}
                  />
                )
              })()}
            </div>
          </Box>

          {/* Arrival time + service duration + time window */}
          <Box className={classes.metaBlock}>
            <Typography className={classes.arrivalTime}>
              {formatTime(stop.estimatedArrival)}
            </Typography>
            {stop.serviceMinutes ? (
              <Chip
                label={`${stop.serviceMinutes} min`}
                size="small"
                className={classes.durationChip}
              />
            ) : null}
            {stop.timeWindow?.start && stop.timeWindow?.end && (
              <Chip
                icon={<AccessTimeIcon style={{ fontSize: 12 }} />}
                label={`${formatTime(stop.timeWindow.start)} - ${formatTime(stop.timeWindow.end)}`}
                size="small"
                style={{
                  height: 18,
                  fontSize: '0.62rem',
                  fontWeight: 600,
                  backgroundColor: stop.timeWindowWarning ? '#FFEBEE' : '#E8F5E9',
                  color: stop.timeWindowWarning ? '#C62828' : '#2E7D32',
                  border: `1px solid ${stop.timeWindowWarning ? '#C62828' : '#2E7D32'}`,
                }}
              />
            )}
          </Box>
        </div>
      )}
    </Draggable>
  )
}

export default StopListItem
